import ImmutableGameTree from '@sabaki/immutable-gametree';
import { GameTree, Vertex } from '@/shared/ui/GOViewer/model/types';
import { formatCoordinates, parseCoordinates } from '@/shared/ui/GOViewer/model/utils';
import GoBoard from '@sabaki/go-board';
import type { MarkerObj, Map as ShudanMap } from '@sabaki/shudan';
import type { DrawingLine } from '@/entities/game-board/canvas/model/types';
import { BroadcastNode, Color, CurrentMove, GameSettings, PlayerInfo } from './types';
import { BoardPositionCache, defaultBoardPositionCache } from './position-cache';

export function getRootGameProperties(
    settings: GameSettings,
    blackPlayer: PlayerInfo,
    whitePlayer: PlayerInfo
): Record<string, string | undefined> {
    const komiDivisor = settings.komi.unit === 1 ? 100 : 50;
    const normalizedRules = settings.rules.toLowerCase();

    return {
        KM: String(settings.komi.value / komiDivisor),
        RU: normalizedRules.charAt(0).toUpperCase() + normalizedRules.slice(1),
        PB: blackPlayer.nick || undefined,
        PW: whitePlayer.nick || undefined,
        BR: blackPlayer.rank,
        WR: whitePlayer.rank,
    };
}

export function getRootTournamentProperties(
    tournament?: {
        title?: string;
        round?: number;
    } | null,
    createdAt?: string
): Record<string, string | undefined> {
    const date = createdAt?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];

    return {
        EV: tournament?.title || undefined,
        RO: tournament?.round !== undefined ? String(tournament.round) : undefined,
        DT: date,
    };
}

export function replaceRootGameProperties(
    draft: Parameters<Parameters<GameTree['mutate']>[0]>[0],
    properties: Record<string, string | undefined>
) {
    for (const [property, value] of Object.entries(properties)) {
        draft.removeProperty(draft.root.id, property);
        if (value !== undefined) draft.addToProperty(draft.root.id, property, value);
    }
}

export const METADATA_TO_SGF: Record<string, string> = {
    label: 'LB',
    circle: 'CR',
    triangle: 'TR',
    square: 'SQ',
    'smile-up': 'U',
    'smile-down': 'D',
    drawings: 'DD',
    editors: '_EDITORS',
    allowed: '_ALLOWED',
    donations: '_DONATIONS',
    'donation-event': '_DONATION_EVENT',
};

export const getCurrentMove = ({ tree, currentId }: { tree: GameTree; currentId: number }) => {
    const currentNode = tree.get(currentId);

    if (!currentNode) return currentNode;

    const nodes = Array.from(tree.listNodesVertically(currentId, -1, {}));
    const move = nodes.find((node) => node.data.B || node.data.W);
    const turnNode = nodes.find((node) => node.data.PL || node.data.B || node.data.W);
    const handicap = tree.root.data.H?.[0];
    let sign: -1 | 1 = turnNode?.data.B ? -1 : turnNode?.data.W ? 1 : handicap && Number(handicap) > 1 ? -1 : 1;
    if (turnNode?.data.PL) sign = turnNode.data.PL[0] === 'W' ? -1 : 1;
    const rawCoord = move?.data.B?.[0] ?? move?.data.W?.[0];
    const vertex: Vertex = rawCoord && rawCoord !== '' ? parseCoordinates(rawCoord) : [-1, -1];
    const number = nodes.reduce((count, node) => count + (node.data.B || node.data.W ? 1 : 0), 0) || undefined;

    return {
        vertex,
        sign,
        number,
    };
};

export function appendNodeToTree(
    tree: GameTree,
    parentId: number,
    color: Color,
    coord: string
): { tree: GameTree; lastId: number } {
    const prop = color === 1 ? 'B' : 'W';

    const parent = tree.get(parentId);
    const existing = parent?.children.find((c) => c.data[prop]?.[0] === coord);
    if (existing) return { tree, lastId: existing.id };

    let lastId = parentId;
    const newTree = tree.mutate((draft) => {
        const id = draft.appendNode(parentId, { [prop]: [coord] }, { disableMerging: true });
        if (id !== null) lastId = id;
    });
    return { tree: newTree, lastId };
}

export function isLastMovePass({ tree, currentId }: { tree: GameTree; currentId: number | null }) {
    if (currentId === null) return false;
    const currentNode = tree.get(currentId);

    return currentNode?.data.B?.[0] === '' || currentNode?.data.W?.[0] === '';
}

const MARKER_PROPS: Record<string, string> = {
    TR: 'triangle',
    CR: 'circle',
    SQ: 'square',
    U: 'smile-up',
    D: 'smile-down',
};

function getMarkerMap(tree: GameTree, nodeId: number): ShudanMap<MarkerObj> | null {
    const node = tree.get(nodeId);
    if (!node) return null;

    const boardSize = Number(tree.get(tree.root.id)?.data?.SZ?.[0]) || 19;
    const rows: (MarkerObj | null)[][] = Array.from({ length: boardSize }, () => new Array(boardSize).fill(null));
    let hasAny = false;

    for (const [sgfProp, markerType] of Object.entries(MARKER_PROPS)) {
        const coords: string[] | undefined = node.data[sgfProp];
        if (!coords) continue;
        coords.forEach((coord) => {
            if (coord.length !== 2) return;
            const [x, y] = parseCoordinates(coord);
            if (x >= 0 && y >= 0 && x < boardSize && y < boardSize) {
                rows[y][x] = { type: markerType } as MarkerObj;
                hasAny = true;
            }
        });
    }

    // LB values are "coord:label" strings
    const lb: string[] | undefined = node.data.LB;
    if (lb) {
        lb.forEach((entry) => {
            const colonIdx = entry.lastIndexOf(':');
            if (colonIdx === -1) return;
            const coord = entry.slice(0, colonIdx);
            if (coord.length !== 2) return;
            const label = entry.slice(colonIdx + 1);
            const [x, y] = parseCoordinates(coord);
            if (x >= 0 && y >= 0 && x < boardSize && y < boardSize) {
                rows[y][x] = { type: 'label', label } as MarkerObj;
                hasAny = true;
            }
        });
    }

    return hasAny ? (rows as unknown as ShudanMap<MarkerObj>) : null;
}

function getDrawings(tree: GameTree, nodeId: number): DrawingLine[] {
    const dd = tree.get(nodeId)?.data.DD as string[] | undefined;
    if (!dd?.length) return [];
    return dd
        .map((entry): DrawingLine | null => {
            const content = entry.slice(1, -1); // strip [ ]
            const first = content.indexOf('|');
            const last = content.lastIndexOf('|');
            if (first === -1 || first === last) return null;
            return {
                points: content.slice(0, first).split(','),
                color: content.slice(first + 1, last),
                width: parseInt(content.slice(last + 1), 10),
            };
        })
        .filter((l): l is DrawingLine => l !== null);
}

export function buildTreeFromBroadcast(
    broadcastTree: BroadcastNode,
    targetNodeId: number
): { tree: GameTree; currentId: number; currentNodeColor: number; bidToGameNodeId: Map<number, number> } {
    let tree = new ImmutableGameTree() as GameTree;
    let currentId = tree.root.id as number;
    let currentNodeColor = 0;

    if (broadcastTree.id === targetNodeId) {
        currentId = tree.root.id as number;
        currentNodeColor = broadcastTree.mv.col;
    }

    const bidMap = new Map<number, number>(); // gameNodeId → broadcastNodeId
    const mdMap = new Map<number, Record<string, string>>(); // gameNodeId → md
    bidMap.set(tree.root.id as number, broadcastTree.id);
    if (broadcastTree.md) mdMap.set(tree.root.id as number, broadcastTree.md);

    function traverse(node: BroadcastNode, parentGameId: number) {
        for (const child of node.ch ?? []) {
            const { mv, setup } = child;
            if (mv.col === 0 && !setup) continue;

            let gameNodeId = parentGameId;
            if (setup) {
                tree = tree.mutate((draft) => {
                    const id = draft.appendNode(parentGameId, setup, { disableMerging: true });
                    if (id !== null) gameNodeId = id;
                });
            } else {
                const result = appendNodeToTree(tree, parentGameId, mv.col as Color, formatCoordinates([mv.c, mv.r]));
                tree = result.tree;
                gameNodeId = result.lastId;
            }
            bidMap.set(gameNodeId, child.id);
            if (child.md) mdMap.set(gameNodeId, child.md);

            if (child.id === targetNodeId) {
                currentId = gameNodeId;
                currentNodeColor = mv.col;
            }

            traverse(child, gameNodeId);
        }
    }

    traverse(broadcastTree, tree.root.id as number);

    tree = tree.mutate((draft) => {
        for (const [key, values] of Object.entries(broadcastTree.setup ?? {})) {
            values.forEach((value) => draft.addToProperty(draft.root.id, key, value));
        }
        for (const [gameId, broadcastId] of bidMap) {
            draft.addToProperty(gameId, '_BID', String(broadcastId));
        }
        for (const [gameId, md] of mdMap) {
            for (const [key, jsonValue] of Object.entries(md)) {
                const sgfProp = METADATA_TO_SGF[key];
                if (!sgfProp) continue;
                const values: string[] = JSON.parse(jsonValue);
                values.forEach((v) => draft.addToProperty(gameId, sgfProp, v));
            }
        }
    });

    const bidToGameNodeId = new Map<number, number>();
    for (const [gameId, broadcastId] of bidMap) {
        bidToGameNodeId.set(broadcastId, gameId);
    }

    return { tree, currentId, currentNodeColor, bidToGameNodeId };
}

export function getBroadcastMainLineEndId(tree: BroadcastNode): number {
    let node = tree;
    while (node.ch?.length) node = node.ch[0];
    return node.id;
}

export function resolveVisual(
    tree: GameTree,
    effectiveId: number,
    fallback: { board: GoBoard; currentMove: CurrentMove | null },
    positionCache: BoardPositionCache = defaultBoardPositionCache
): {
    board: GoBoard;
    currentMove: CurrentMove | null;
    markerMap: ShudanMap<MarkerObj> | null;
    drawings: DrawingLine[];
} {
    const position = positionCache.resolve(tree, effectiveId);
    positionCache.scheduleWarm(tree);

    return {
        board: position?.board ?? fallback.board,
        currentMove: position?.currentMove ?? getCurrentMove({ tree, currentId: effectiveId }) ?? fallback.currentMove,
        markerMap: getMarkerMap(tree, effectiveId),
        drawings: getDrawings(tree, effectiveId),
    };
}

type Result = {
    winner: number;
    reason: number;
    score: { value: number; unit: 0 | 1 };
};

export const getResultString = (result: Result) => {
    let resultString: string = '';

    if (result.winner === 1) {
        resultString = 'B+';
    } else if (result.winner === 2) {
        resultString = 'W+';
    }

    switch (result.reason) {
        case 1: {
            resultString += 'R';
            break;
        }
        case 2: {
            const scoreDivisor = result.score.unit === 0 ? 50 : 100;
            const score = result.score.value / scoreDivisor;
            resultString += `${score}`;
            break;
        }
        case 3: {
            resultString += 'T';
            break;
        }
        default: {
            resultString += '';
            break;
        }
    }

    return resultString;
};

export const formatOvertime = (seconds: number, periods: number, t: (key: string) => string) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    let timeStr: string;

    if (days > 0) {
        timeStr = `${days}${t('daysFirstLetter')} ${hours}${t('hoursFirstLetter')}`;
    } else if (hours > 0) {
        timeStr = `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
        timeStr = `${minutes}:${secs.toString().padStart(2, '0')}`;
    }

    return `${timeStr} (${periods})`;
};

export const formatTime = (seconds: number, t: (key: string) => string) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) {
        if (hours > 0) {
            return `${days}${t('daysFirstLetter')} ${hours}${t('hoursFirstLetter')}`;
        }
        return `${days}${t('daysFirstLetter')}`;
    }

    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${secs.toString().padStart(2, '0')}`;
};
